import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { LocationStrategy } from '@angular/common';
import { PreviousRouteService } from 'src/app/services/previousRouteService';
import ServiceRequestModel from 'src/app/services/serviceRequest.model';
import SrTopicsModel from 'src/app/services/srTopics.model';
import BusinessEntity from 'src/app/services/businessEntity.model';
import PersonsModel from 'src/app/services/persons.model';
import SrStatusCodesModel from 'src/app/services/srStatusCodes.model';
import CustomerDeviceModel from 'src/app/services/customerDevice.model';
import Device from 'src/app/services/device.model';
import { ServiceRequestService } from 'src/app/services/serviceRequest.service';
import { BusinessEntityService } from 'src/app/services/businessEntity.service';
import { PersonsService } from 'src/app/services/persons.service';
import { DeviceService } from 'src/app/services/device.service';
import { SrTopicsService } from 'src/app/services/sr-topics.service';
import { SrStatusCodesService } from 'src/app/services/sr-status-codes.service';
import { CustomerDeviceService } from 'src/app/services/customerDevice.service';
import { BatchLoader } from 'src/app/Utils/batch-loader.util';

@Component({
  selector: 'app-service-requests',
  templateUrl: './service-requests.component.html',
  styleUrls: ['./service-requests.component.scss']
})
export class ServiceRequestsComponent implements OnInit, OnDestroy {
  public serviceRequests: ServiceRequestModel[] = [];
  public serviceRequestsList: ServiceRequestModel[] = [];
  public serviceRequest: ServiceRequestModel = new ServiceRequestModel();
  public filteredServiceRequests: ServiceRequestModel[] = [];
  public srTopics: Array<SrTopicsModel> = [];
  public persons: Array<PersonsModel> = [];
  public businessEntities: Array<BusinessEntity> = [];
  public srCodes: Array<SrStatusCodesModel> = [];
  public customerDevices: Array<CustomerDeviceModel> = [];
  public devices: Array<Device> = [];
  public search: string = '';
  public submitButton: boolean = false;
  public updateButton: boolean = false;
  public serviceRequestDoubleClick: boolean = false;
  public NoDataMessage: boolean = false;
  public loginPersonType: string;
  public loginUserBusinessName: string;
  public appPermission: any;
  public registerForm: FormGroup;
  public submitted = false;
  public enableDisablePageLoader: boolean = false;
  public listLoader = false;
  public isFiltering: boolean = false;
  public noList: boolean = false;
  public showForm: boolean = false;
  public formFieldCount: number = 0;

  private searchTimeout: any;
  private scrollTimeout: any;
  private batchLoader: BatchLoader<any>;

  constructor(
    public serviceRequestService: ServiceRequestService,
    public srTopicsService: SrTopicsService,
    public businessEntityService: BusinessEntityService,
    public srStatusCodesService: SrStatusCodesService,
    public personsService: PersonsService,
    public customerDeviceService: CustomerDeviceService,
    public route: ActivatedRoute,
    public deviceService: DeviceService,
    public formBuilder: FormBuilder,
    private locationStrategy: LocationStrategy,
    private previousRouteService: PreviousRouteService,
    private toastr: ToastrService
  ) {
    this.serviceRequest = new ServiceRequestModel();
    this.registerForm = this.formBuilder.group({
      issuetype: ['', [Validators.required]],
      assignedto: [null],
      farmname: ['', [Validators.required]],
      serialno: ['', [Validators.required]],
      desc: ['', [Validators.maxLength(360)]],
      status: [null],
    });
    this.formFieldCount = Object.keys(this.registerForm.controls).length;
  }

  public async ngOnInit() {
    try {
      window.scrollTo(0, 0);
      this.enableDisablePageLoader = true;
      this.clear();
      this.search = '';
      this.serviceRequests = [];
      this.serviceRequestsList = [];
      this.filteredServiceRequests = [];
      this.isFiltering = false;
      this.previousRouteService.setPreviousRoute(this.locationStrategy.path());
      this.appPermission = this.route.snapshot.data['rolepermission'] || [{ edit: true, assign: true, status: true }];
      this.loginPersonType = localStorage.getItem('loginPersonType');
      this.loginUserBusinessName = localStorage.getItem('loginBusinessName');

      await this.loadData();
      this.initializeBatchLoader();
      this.batchLoader?.reset();
      await this.batchLoader?.loadNextBatch(true);
      this.enableDisablePageLoader = false;
    } catch (error: any) {
      this.toastr.error(error);
      this.enableDisablePageLoader = false;
    }
  }

  private async loadData() {
    if (this.loginPersonType !== 'Business Admin' && this.loginPersonType !== 'Farmer' && this.loginPersonType !== 'Farm Manager') {
      const [requests, topics, codes, customerPersons, businesses, devs] = await Promise.all([
        this.serviceRequestService.getAllServiceRequests(),
        this.srTopicsService.getAllSrTopics(),
        this.srStatusCodesService.getAllSrStatusCodes(),
        this.personsService.getAllPersons(),
        this.businessEntityService.getAllBusiness(),
        this.deviceService.getAllDevices()
      ]);
      this.serviceRequests = requests || [];
      this.srTopics = topics || [];
      this.srCodes = codes || [];
      this.businessEntities = businesses || [];
      this.devices = devs || [];
      this.persons = (customerPersons || []).filter((person: any) => person && person.personType[0] && person.personType[0].personRoleID === '630c4d39f4bcf9001e52805e');
    } else {
      const [requests, topics, codes, customerPersons, devs, businesses] = await Promise.all([
        this.serviceRequestService.getSRByLoginBusinessId(localStorage.businessID),
        this.srTopicsService.getAllSrTopics(),
        this.srStatusCodesService.getAllSrStatusCodes(),
        this.personsService.getAllPersons(),
        this.customerDeviceService.getAlladdMapDeviceWithFarm(localStorage.businessID),
        this.businessEntityService.getAllBusiness(),
      ]);
      this.serviceRequests = requests || [];
      this.srTopics = topics || [];
      this.srCodes = codes || [];
      this.customerDevices = devs || [];
      this.businessEntities = businesses || [];
      this.persons = (customerPersons || []).filter((person: any) => person && person.personType[0] && person.personType[0].personRoleID === '630c4d39f4bcf9001e52805e');
    }
    this.changeServiceRequest();
  }

  public changeServiceRequest() {
    if (!this.serviceRequests) return;
    this.serviceRequests.forEach(req => {
      if (req.topic_id && this.srTopics) {
        req.srTopics = this.srTopics.find(t => String(t.id) === String(req.topic_id));
      }
      if (req.ss_code_id && this.srCodes) {
        req.ssCode = this.srCodes.find(c => String(c.id) === String(req.ss_code_id));
      }
      if (req.device_id && this.devices) {
        req.device = this.devices.find(d => String(d.id) === String(req.device_id));
      }
    });
  }

  @HostListener('window:scroll', [])
  onScroll(): void {
    this.checkScrollAndLoad();
  }

  private checkScrollAndLoad(): void {
    clearTimeout(this.scrollTimeout);
    this.scrollTimeout = setTimeout(() => {
      if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 100 && !this.batchLoader?.isLoading()) {
        this.batchLoader.loadNextBatch();
      }
    }, 50);
  }

  private initializeBatchLoader(): void {
    this.batchLoader = new BatchLoader<any>(
      () => this.isFiltering ? this.filteredServiceRequests : this.serviceRequests,
      (updatedList) => {
        this.serviceRequestsList = updatedList;
        this.noList = updatedList.length === 0;
      },
      undefined,
      (loading) => this.listLoader = loading
    );
  }

  public ngOnDestroy(): void {
    this.serviceRequestService.cancelOnGoingHttpRequests();
    this.srTopicsService.cancelOnGoingHttpRequests();
    this.srStatusCodesService.cancelOnGoingHttpRequests();
    this.businessEntityService.cancelOnGoingHttpRequests();
    this.personsService.cancelOnGoingHttpRequests();
    this.customerDeviceService.cancelOnGoingHttpRequests();
    this.deviceService.cancelOnGoingHttpRequests();
    this.batchLoader?.markAsDestroyed();
    this.batchLoader?.reset();
  }

  get srFormValidation() {
    return this.registerForm.controls;
  }

  public onSearchChanged(): void {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.applySearch();
    }, 300);
  }

  public async applySearch(): Promise<void> {
    const value = this.search && this.search.trim().toLowerCase();
    this.isFiltering = !!value;

    const sourceList = this.isFiltering
      ? this.serviceRequests.filter(req =>
          (req && req.BusinessDetails && req.BusinessDetails[0] && req.BusinessDetails[0].businessName && req.BusinessDetails[0].businessName.toLowerCase().includes(value)) ||
          (req && req.srTopics && req.srTopics.name && req.srTopics.name.toLowerCase().includes(value)) ||
          (req && req.device && req.device.serial_number && req.device.serial_number.toLowerCase().includes(value)) ||
          (req && req.ssCode && req.ssCode.code && req.ssCode.code.toLowerCase().includes(value))
        )
      : this.serviceRequests;

    this.filteredServiceRequests = this.isFiltering ? sourceList : [];
    this.batchLoader?.reset(true);
    await this.batchLoader?.loadNextBatch(true);
    if (sourceList.length === 0) {
      this.noList = true;
      this.serviceRequestsList = [];
      return;
    }
    setTimeout(() => this.checkScrollAndLoad(), 100);
  }

  public openForm() {
    this.showForm = true;
    this.submitButton = true;
    this.updateButton = false;
    this.submitted = false;
    this.serviceRequest = new ServiceRequestModel();
    this.registerForm.reset();
  }

  public closeForm() {
    this.showForm = false;
    this.submitted = false;
    this.serviceRequest = new ServiceRequestModel();
    this.registerForm.reset();
  }

  public clear() {
    this.closeForm();
  }

  public async addServiceRequest() {
    this.submitted = true;
    if (this.registerForm.invalid) return;
    try {
      this.serviceRequestDoubleClick = true;
      const formVal = this.registerForm.value;
      this.serviceRequest.topic_id = formVal.issuetype;
      this.serviceRequest.assignedTo = formVal.assignedto;
      this.serviceRequest.business_id = formVal.farmname;
      this.serviceRequest.device_id = formVal.serialno;
      this.serviceRequest.description = formVal.desc;

      await this.serviceRequestService.create(this.serviceRequest);
      this.toastr.success('Service Request Added Successfully');
      this.closeForm();
      await this.ngOnInit();
    } catch (error: any) {
      this.toastr.error(error);
      this.serviceRequestDoubleClick = false;
    }
  }

  public async updateServiceRequest() {
    this.submitted = true;
    if (this.registerForm.invalid) return;
    try {
      this.serviceRequestDoubleClick = true;
      const formVal = this.registerForm.value;
      const payload: any = {
        topic_id: formVal.issuetype,
        assignedTo: formVal.assignedto,
        description: formVal.desc,
        ss_code_id: formVal.status
      };
      await this.serviceRequestService.updateOneByID(this.serviceRequest.id, payload);
      await this.serviceRequestService.sendMailRequest(this.serviceRequest.id, localStorage.getItem('LoginUserName') || '');
      this.toastr.success('Service Request Updated Successfully');
      this.closeForm();
      await this.ngOnInit();
    } catch (error: any) {
      this.toastr.error(error);
      this.serviceRequestDoubleClick = false;
    }
  }

  public getServiceRequestById(id: string) {
    this.showForm = true;
    this.submitButton = false;
    this.updateButton = true;
    this.submitted = false;
    this.serviceRequestDoubleClick = false;
    const found = this.serviceRequests.find(req => req && req.id === id);
    if (found) {
      this.serviceRequest = Object.assign({}, found);
      this.registerForm.patchValue({
        issuetype: this.serviceRequest.topic_id,
        assignedto: this.serviceRequest.assignedTo,
        farmname: this.serviceRequest.business_id,
        serialno: this.serviceRequest.device_id,
        desc: this.serviceRequest.description,
        status: this.serviceRequest.ss_code_id
      });
    }
  }

  public async getAllMapDeviceWithBusiness(businessId: string) {
    if (!businessId) return;
    try {
      this.customerDevices = await this.customerDeviceService.getAlladdMapDeviceWithFarm(businessId);
    } catch (error: any) {
      this.toastr.error(error);
    }
  }
}
